import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useCurrentUser } from './useCurrentUser.js';

/**
 * Returns the user's currently-selected building ({ id, code, name } | null).
 * Re-runs when useCurrentUser re-renders.
 */
export function useCurrentBuilding() {
  const { data: user } = useCurrentUser();
  return user?.currentBuilding ?? null;
}

/**
 * Mutation wrapper for POST /auth/switch-building.
 *
 * Switching buildings has to refresh EVERY page in the app — the user is now
 * looking at a different warehouse. Two layers of cache invalidation:
 *
 *   1. /auth/me — refetched first (and synchronously awaited) so
 *      useCurrentBuilding() resolves to the new building before any module
 *      pages re-render. Otherwise the new page mounts with the old
 *      building.id and re-queries for stale data.
 *   2. Every other query — invalidated. Most modules (Spend, Maintenance,
 *      Workforce, etc.) scope their server queries via the session's
 *      currentBuildingId header without including building.id in the
 *      TanStack Query key. That means a cached LAX response would keep
 *      serving even after the user switched to ORD. A blanket invalidate
 *      forces every active query to refetch; inactive ones get marked
 *      stale and refetch on next mount.
 *
 * Trade-off accepted: blanket invalidation is heavy (it refetches data that
 * doesn't depend on building, like the buildings list itself). But correctness
 * across every module beats incremental refetch optimization. If we want to
 * trim later, the proper fix is to add `building.id` to every module's query
 * key — a sweeping refactor we can do once we feel the cost.
 *
 * Callers handle their own toast/error UI.
 */
export function useSwitchBuilding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (buildingId) => {
      const result = await api.post('/auth/switch-building', { buildingId });
      // Refresh /auth/me first and AWAIT it so the new currentBuilding is
      // visible to the very next render of every page.
      await qc.refetchQueries({ queryKey: ['auth', 'me'] });
      return result;
    },
    onSuccess: () => {
      // Invalidate every other query so server-scoped reads (Spend dashboard,
      // Maintenance outages, Workforce attendance grid, etc.) refetch with
      // the new currentBuilding. /auth/me was already refreshed above, so
      // skip it here to avoid a redundant network call.
      qc.invalidateQueries({
        predicate: (q) => q.queryKey?.[0] !== 'auth',
      });
    },
  });
}
